import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/glitch-ui/data-table';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import { useAdPerformanceReportQuery, metaCreativePerformanceQueryOptions, metaIntegrationStatusQueryOptions } from '@/lib/query-options';
import { formatMoney, toDisplayAmount } from '@/lib/performance/money';
import { deriveMetrics, formatRate, formatRoas } from '@/lib/performance/metrics';
import { pivotLeaves, availableDimensions, PIVOT_DIMENSIONS, type PivotDimension, type PivotLeaf, type PivotRow } from '@/lib/performance/pivot';
import { dateWindow } from '@/lib/performance/date-window';
import { platformsInScope, type PlatformScope } from '@/lib/performance/types';
import { PlatformLogo } from '@/lib/performance/platform';
import { MetaPerformanceBadge } from '@/components/campaign/meta/meta-metrics';
import type { MetaPerformanceStatus } from '@/lib/types';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { usePerformanceData, useScopedCampaigns } from '@/hooks/usePerformanceData';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ColumnsPopover } from '../cells';
import { AdsTab, type AdsSubView } from '../tabs/ads-tab';
import {
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceGroupByAtom,
  consolidatedPerformanceComparisonAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceIncludeExternalAtom,
} from '@/store';

// Column-picker metadata for the Ads table. Duplicated from perf-v2
// (performance-v2.tsx:122-136) so the Google Ads view can drive the same
// column-visibility hook; de-dup tracked in #5994.
const ADS_TABLE_COLUMNS: { id: string; label: string; pinned?: boolean }[] = [
  { id: 'ad_name',     label: 'Ad',          pinned: true },
  { id: 'headlines',   label: 'Headlines' },
  // Consolidated Imp · Clicks · Conv · Cost cell. On by default; users can
  // switch back to individual columns via the column picker for delta arrows.
  { id: 'performance', label: 'Performance' },
  { id: 'impressions', label: 'Impressions' },
  { id: 'clicks',      label: 'Clicks' },
  { id: 'ctr',         label: 'CTR' },
  { id: 'conversions', label: 'Conversions' },
  { id: 'cost',        label: 'Cost' },
  { id: 'cpa',         label: 'CPA' },
];
const ADS_COLUMN_IDS = new Set(ADS_TABLE_COLUMNS.map(c => c.id));
const ADS_PINNED_COLUMN_IDS = new Set(ADS_TABLE_COLUMNS.filter(c => c.pinned).map(c => c.id));

// perf-v2 DEFAULT_LAYOUTS.ads tab id under the consolidated shell's shared
// widget-layout atom (see CONSOLIDATED_DEFAULT_LAYOUTS).
const GOOGLE_ADS_LAYOUT_KEY = 'google-ads';

function numOrNull(value: string | null | undefined): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// Meta-native per-ad extras (Frequency, winner/loser Status, Purchases) + the Google
// campaign subtitle — kept OUT of the generic pivot (pivot.ts stays platform-agnostic)
// and looked up by the ad's pivot key when grouping by Ad.
interface AdExtras {
  frequency: number | null;
  status: MetaPerformanceStatus | null;
  campaign: string | null;
  purchases: number;
  costPerPurchaseCents: number | null;
}

/**
 * Unified Creative & Ads table (#5971, CP-8; #6040, T2). A dimension pivot over the
 * same ad-level data (Google ads + Meta creatives), grouped by Ad (default) or
 * Platform, rendering the canonical metric block (metrics.ts). Grouped by Ad under
 * Meta scope it also shows the Meta-native Frequency + winner/loser Status. Campaign
 * and Ad-group grouping live on the Campaigns tab — Meta creative rows carry no
 * campaign linkage — so this panel only offers Ad + Platform.
 */
export function CreativeAdsPanel({ scope }: { scope: PlatformScope }) {
  const inScope = platformsInScope(scope);
  const googleInScope = inScope.includes('google');
  const metaInScope = inScope.includes('meta');
  const { symbol } = useCurrencySymbol();
  const cs = symbol ?? '';

  // Honor the toolbar date range through the shared exact-day window (#6274).
  // Meta stays snapshot-backed.
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const window = useMemo(() => dateWindow(selectedDays), [selectedDays]);

  // #6277 — same campaign scoping as Google scope (was account-wide `undefined`,
  // so the scope switch changed the Google numbers and filters were inert here).
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);
  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);
  const { scopedGoogleAdsIds } = useScopedCampaigns({ includeExternal, typeFilter, selectedCampaignIds });

  // #6278 — Meta money renders in the Meta AD ACCOUNT's currency (the Audience
  // panel's #6182 sourcing); one org can run differently-billed accounts.
  const { data: metaStatus } = useQuery({ ...metaIntegrationStatusQueryOptions(), enabled: metaInScope });
  const { symbol: metaSymbol } = useCurrencySymbol(undefined, metaStatus?.selected_ad_account?.currency);
  const metaCs = metaSymbol ?? cs;
  const symFor = (p: PivotRow['platform']) => (p === 'meta' ? metaCs : cs);


  const { data: adData, isLoading: googleLoading } = useAdPerformanceReportQuery(
    scopedGoogleAdsIds || undefined,
    undefined,
    {
      startDate: window.start_date,
      endDate: window.end_date,
      // Disabled when the filter matches nothing — an absent campaign_ids
      // param means account-wide to the backend (#6275).
      enabled: googleInScope && scopedGoogleAdsIds.length > 0,
    },
  );
  const { data: metaResp, isLoading: metaLoading } = useQuery(metaCreativePerformanceQueryOptions(metaInScope));

  // Platform grouping omitted — the top-level scope switch already covers it.
  const dimensions = useMemo(() => availableDimensions(scope, ['ad']), [scope]);
  const [groupBy, setGroupBy] = useState<PivotDimension>('ad');
  const activeDimension = dimensions.includes(groupBy) ? groupBy : (dimensions[0] ?? 'ad');

  // Flatten both platforms into ad-level leaves; keep Meta-only extras in a side map
  // keyed by the ad's pivot key so pivot.ts stays platform-agnostic.
  const { leaves, extras } = useMemo(() => {
    const out: PivotLeaf[] = [];
    const ex = new Map<string, AdExtras>();
    if (googleInScope && adData) {
      for (const ad of adData) {
        out.push({
          platform: 'google',
          campaignId: ad.campaign_id,
          campaignName: ad.campaign_name || '—',
          adGroupId: ad.ad_group_id,
          adGroupName: ad.ad_group_name || ad.ad_group_id,
          adId: ad.ad_id,
          adName: ad.ad_name || ad.headlines?.[0] || ad.ad_id,
          status: null,
          impressions: ad.impressions ?? 0,
          clicks: ad.clicks ?? 0,
          results: ad.conversions ?? 0,
          spend: ad.cost ?? 0,
          conversionValue: ad.all_conversions_value ?? 0,
        });
        ex.set(`google:${ad.ad_id}`, { frequency: null, status: null, campaign: ad.campaign_name || null, purchases: 0, costPerPurchaseCents: null });
      }
    }
    if (metaInScope && metaResp) {
      for (const m of metaResp.rows) {
        out.push({
          platform: 'meta',
          campaignId: '',
          campaignName: '—',
          adGroupId: null,
          adGroupName: null,
          adId: m.meta_ad_id,
          adName: m.headline || m.name || m.meta_ad_id,
          status: null,
          impressions: m.impressions,
          clicks: m.clicks,
          results: m.leads,
          spend: toDisplayAmount(m.spend_cents, 'meta'),
          conversionValue: null,
        });
        ex.set(`meta:${m.meta_ad_id}`, { frequency: numOrNull(m.frequency), status: m.performance_status ?? null, campaign: null, purchases: m.purchases, costPerPurchaseCents: m.cost_per_purchase_cents ?? null });
      }
    }
    return { leaves: out, extras: ex };
  }, [googleInScope, adData, metaInScope, metaResp]);

  const rows = useMemo(() => pivotLeaves(leaves, activeDimension), [leaves, activeDimension]);

  const columns = useMemo<ColumnDef<PivotRow, unknown>[]>(() => {
    const isAd = activeDimension === 'ad';
    const cols: ColumnDef<PivotRow, unknown>[] = [];
    // Thin, sortable platform-logo column pinned to the left of the creative name —
    // only under All scope (mixed platforms).
    if (scope === 'all') {
      cols.push({
        id: 'platform',
        accessorFn: (r) => r.platform,
        header: '',
        cell: ({ row }) => <PlatformLogo platform={row.original.platform} className="h-4 w-4" />,
        meta: { className: 'pl-4 pr-1 w-8' },
      });
    }
    cols.push({
      accessorKey: 'label',
      header: PIVOT_DIMENSIONS[activeDimension].columnHeader,
      cell: ({ row }) => {
        const campaign = isAd ? extras.get(row.original.key)?.campaign : null;
        return (
          <div className="min-w-0">
            <p className="truncate font-medium text-gray-900">{row.original.label}</p>
            {campaign && <p className="truncate text-xs text-gray-500">{campaign}</p>}
          </div>
        );
      },
    });
    cols.push(
      // Layer 2 — volume
      { accessorKey: 'impressions', header: 'Impr.', cell: ({ row }) => row.original.impressions.toLocaleString() },
      { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
      {
        accessorKey: 'results',
        header: scope === 'meta' ? 'Leads' : 'Conv.',
        cell: ({ row }) =>
          scope === 'meta'
            ? Math.round(row.original.results).toLocaleString()
            : row.original.resultKind === 'leads'
              ? `${Math.round(row.original.results).toLocaleString()} leads`
              : Math.round(row.original.results).toLocaleString(),
      },
      // Layer 1 — money (ROAS is "—" for Meta until #6044)
      { accessorKey: 'spend', header: 'Spend', cell: ({ row }) => formatMoney(row.original.spend, symFor(row.original.platform)) },
      { id: 'roas', accessorFn: (r) => deriveMetrics(r).roas, header: 'ROAS', cell: ({ row }) => formatRoas(deriveMetrics(row.original).roas) },
      { id: 'cpc', accessorFn: (r) => deriveMetrics(r).cpc, header: 'CPC', cell: ({ row }) => formatMoney(deriveMetrics(row.original).cpc, symFor(row.original.platform)) },
      // Layer 3 — rates
      { id: 'ctr', accessorFn: (r) => deriveMetrics(r).ctr, header: 'CTR', cell: ({ row }) => formatRate(deriveMetrics(row.original).ctr, 2) },
      { id: 'imprToConv', accessorFn: (r) => deriveMetrics(r).imprToConv, header: 'Impr→Conv.', cell: ({ row }) => formatRate(deriveMetrics(row.original).imprToConv, 2) },
      { id: 'clickToConv', accessorFn: (r) => deriveMetrics(r).clickToConv, header: 'Conv. rate', cell: ({ row }) => formatRate(deriveMetrics(row.original).clickToConv, 1) },
      // Efficiency
      {
        id: 'cpa',
        accessorFn: (r) => (r.results > 0 ? r.spend / r.results : null),
        header: scope === 'meta' ? 'Cost / lead' : 'Cost / conv.',
        cell: ({ row }) => (row.original.results > 0 ? formatMoney(row.original.spend / row.original.results, symFor(row.original.platform)) : '—'),
      },
    );
    // Meta-native depth: only meaningful per-ad and only under Meta scope (Google rows
    // have no frequency/verdict). Preserves the pre-pivot Meta creative view.
    if (isAd && scope === 'meta') {
      // Purchases are Meta pixel/CAPI e-commerce events — only show the columns when the
      // account actually has any (lead-gen accounts would otherwise carry an all-zero column).
      const hasPurchases = [...extras.values()].some((e) => e.purchases > 0);
      if (hasPurchases) {
        cols.push(
          {
            id: 'purchases',
            header: 'Purchases',
            cell: ({ row }) => (extras.get(row.original.key)?.purchases ?? 0).toLocaleString(),
          },
          {
            id: 'costPerPurchase',
            header: 'Cost / purch.',
            cell: ({ row }) => {
              const c = extras.get(row.original.key)?.costPerPurchaseCents;
              return c != null ? formatMoney(toDisplayAmount(c, 'meta'), cs) : '—';
            },
          },
        );
      }
      cols.push(
        {
          id: 'frequency',
          header: 'Freq.',
          cell: ({ row }) => {
            const f = extras.get(row.original.key)?.frequency;
            return f != null ? f.toFixed(2) : '—';
          },
        },
        {
          id: 'status',
          header: 'Status',
          cell: ({ row }) => {
            const s = extras.get(row.original.key)?.status;
            return s ? <MetaPerformanceBadge status={s} /> : '—';
          },
        },
      );
    }
    return cols;
  }, [cs, metaCs, scope, activeDimension, extras]);

  const loading = (googleInScope && googleLoading) || (metaInScope && metaLoading);
  if (loading) {
    return <div className="flex h-24 items-center justify-center text-sm text-gray-400">Loading…</div>;
  }

  return (
    <div className="space-y-3">
      {dimensions.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Group by</span>
          <Tabs value={activeDimension} onValueChange={(v) => setGroupBy(v as PivotDimension)}>
            <TabsList>
              {dimensions.map((d) => (
                <TabsTrigger key={d} value={d}>
                  {PIVOT_DIMENSIONS[d].label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}
      <DataTable
        data={rows}
        columns={columns}
        pageSize={15}
        emptyState={metaInScope && !googleInScope ? 'No Meta ads in the synced window' : 'No ads with activity in this period'}
      />
      {/* #6278 — Meta creative figures come from the snapshot sync's trailing
          7-day window; only the Google half follows the toolbar range. */}
      {metaInScope && (
        <p className="text-xs text-gray-500">
          Meta figures cover the last 7 days of synced data{googleInScope ? '; Google figures follow the selected date range' : ''}.
        </p>
      )}
    </div>
  );
}

/**
 * The Google-specific Creative & Ads view, reusing perf-v2's AdsTab verbatim
 * (Ads / Headlines / Landing Pages sub-views + column picker). Fed by the shared
 * usePerformanceData hook (the same data layer /performance-v2 uses) so it honors
 * the toolbar filters (#5995) with no duplicated computation. Owns the sub-view
 * toggle + Ads column picker locally; the chart is deferred (renderChart => null,
 * performance-chart widget omitted from the layout) as on the Overview.
 */
function GoogleAdsSection({
  getTabLayout,
  onReorder,
  onResize,
}: {
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  const { symbol } = useCurrencySymbol('google');
  const cs = symbol ?? '';

  const [subView, setSubView] = useState<AdsSubView>('ads');

  const {
    visible: visibleAdsColumns, toggle: toggleAdsColumn, reset: resetAdsColumns,
  } = useColumnVisibility({ settingsKey: 'ads', allIds: ADS_COLUMN_IDS, pinnedIds: ADS_PINNED_COLUMN_IDS });

  // Driven by the shared filter toolbar (#5971/#5995).
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const groupBy = useAtomValue(consolidatedPerformanceGroupByAtom);
  const comparison = useAtomValue(consolidatedPerformanceComparisonAtom);
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);

  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);

  const perf = usePerformanceData({
    includeExternal,
    tab: 'ads',
    campaignSubView: 'by-type',
    comparisonMode: comparison,
    selectedCampaignIds,
    chartDateRange: selectedDays,
    chartGroupBy: groupBy,
    showUserEdits: false,
    selectedMetric: 'conversions',
    selectedDays,
    typeFilter,
    cs,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Tabs value={subView} onValueChange={(v) => setSubView(v as AdsSubView)}>
          <TabsList>
            <TabsTrigger value="ads">Ads</TabsTrigger>
            <TabsTrigger value="headlines">Headlines</TabsTrigger>
            <TabsTrigger value="landing-pages">Landing Pages</TabsTrigger>
          </TabsList>
        </Tabs>
        {subView === 'ads' && (
          <ColumnsPopover
            columnsMeta={ADS_TABLE_COLUMNS}
            visibleIds={visibleAdsColumns}
            totalIds={ADS_COLUMN_IDS}
            onToggle={toggleAdsColumn}
            onReset={resetAdsColumns}
          />
        )}
      </div>
      <AdsTab
        subView={subView}
        adMetrics={perf.adMetrics}
        previousAdMetricsByAdId={perf.previousAdMetricsByAdId}
        activeCampaigns={perf.activeCampaigns}
        visibleColumns={visibleAdsColumns}
        currencySymbol={cs}
        dateRangeLabel={perf.dateRangeLabel}
        dateRangeFilter={perf.dateRangeFilter}
        layout={getTabLayout(GOOGLE_ADS_LAYOUT_KEY)}
        onReorder={(l) => onReorder(GOOGLE_ADS_LAYOUT_KEY, l)}
        onResize={(id, w) => onResize(GOOGLE_ADS_LAYOUT_KEY, id, w)}
        renderChart={() => null}
      />
    </div>
  );
}

/**
 * Consolidated Creative & Ads tab (#5995) — hybrid by scope. Google renders the
 * full-depth perf-v2 AdsTab (Ads / Headlines / Landing Pages sub-views, column
 * picker) fed by the shared usePerformanceData hook; Meta / All keep the existing
 * merged cross-platform CreativeAdsPanel table unchanged.
 */
export function ConsolidatedCreativeAdsPanel({
  scope,
  getTabLayout,
  onReorder,
  onResize,
}: {
  scope: PlatformScope;
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  if (scope === 'google') {
    return <GoogleAdsSection getTabLayout={getTabLayout} onReorder={onReorder} onResize={onResize} />;
  }
  return <CreativeAdsPanel scope={scope} />;
}
